import { X } from "lucide-react";
import { CardZoom } from "@/components/CardZoom";
import { RarityBadge } from "@/components/RarityBadge";
import { formatMoney, type CurrencyCode, type PriceRow } from "@/lib/prices-shared";

const LINE = ["hsl(152 70% 50%)", "hsl(45 95% 58%)", "hsl(205 90% 62%)", "hsl(320 80% 65%)"];

/** Percent-indexed overlay chart so cards at very different prices are comparable. */
function OverlayChart({ rows }: { rows: PriceRow[] }) {
  const w = 640;
  const h = 130;
  const series = rows.map((r) => {
    const first = r.history[0].value || 1;
    return r.history.map((p) => (p.value / first) * 100);
  });
  const all = series.flat();
  const min = Math.min(...all, 100);
  const max = Math.max(...all, 100);
  const span = max - min || 1;
  return (
    <figure className="mt-3">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" role="img" aria-label="30-day indexed price comparison">
        {/* grid so a short or flat comparison never reads as an empty panel */}
        {[0, 1, 2, 3, 4].map((k) => (
          <line key={`hg-${k}`} x1="0" x2={w} y1={8 + (k / 4) * (h - 16)} y2={8 + (k / 4) * (h - 16)} stroke="currentColor" strokeOpacity="0.08" />
        ))}
        {[0, 1, 2, 3, 4, 5, 6].map((k) => (
          <line key={`vg-${k}`} x1={(k / 6) * w} x2={(k / 6) * w} y1="8" y2={h - 8} stroke="currentColor" strokeOpacity="0.06" />
        ))}
        <line x1="0" x2={w} y1={h - ((100 - min) / span) * (h - 16) - 8} y2={h - ((100 - min) / span) * (h - 16) - 8} stroke="hsl(0 0% 60% / 0.35)" strokeDasharray="4 4" />
        {series.map((vals, i) => (
          <polyline
            key={rows[i].code}
            points={vals.map((v, x) => `${(x / (vals.length - 1)) * w},${h - ((v - min) / span) * (h - 16) - 8}`).join(" ")}
            fill="none"
            stroke={LINE[i % LINE.length]}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        ))}
      </svg>
      <figcaption className="mt-1 text-[10px] text-muted-foreground">
        Each line starts at 100 thirty days ago, so the steeper line is the stronger mover regardless of price.
      </figcaption>
    </figure>
  );
}

/**
 * Side-by-side comparison for up to four cards: zoomable scans, the key price
 * columns and an indexed 30-day overlay chart.
 */
export function PriceComparePanel({
  rows,
  currency,
  onClose,
  onRemove,
}: {
  rows: PriceRow[];
  currency: CurrencyCode;
  onClose: () => void;
  onRemove: (code: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Compare card prices">
      <button className="absolute inset-0 bg-background/80 backdrop-blur-sm" aria-label="Close comparison" onClick={onClose} />
      <div className="glass relative max-h-[96vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border p-3 sm:rounded-2xl sm:p-4">
        <button onClick={onClose} className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-card" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-xl font-bold">Compare {rows.length} cards</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Scroll or pinch on any scan to zoom, drag to pan, double-click to reset.
        </p>

        <div className={`mt-3 grid gap-3 ${rows.length > 2 ? "sm:grid-cols-4" : "sm:grid-cols-2"}`}>
          {rows.map((r, i) => (
            <div key={r.code} className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: LINE[i % LINE.length] }} aria-hidden />
                <p className="min-w-0 flex-1 truncate text-sm font-bold">{r.name}</p>
                <button onClick={() => onRemove(r.code)} aria-label={`Remove ${r.name} from comparison`} className="rounded p-1 hover:bg-card">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mx-auto mt-1.5 max-w-[118px]">
                <CardZoom src={r.image} alt={`${r.name} Palworld TCG card ${r.code}`} caption={r.code} cardCode={r.code} cardName={r.name} />
              </div>
              <div className="mt-1.5 space-y-0.5 text-[11px]">
                <p className="flex justify-between"><span className="text-muted-foreground">Market</span><strong className="tabular-nums">{formatMoney(r.market, currency)}</strong></p>
                <p className="flex justify-between"><span className="text-muted-foreground">TCG sold</span><span className="tabular-nums">{r.tcgSoldAvg ? formatMoney(r.tcgSoldAvg, currency) : "—"}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">Index</span><span className="tabular-nums">{formatMoney(r.index, currency)}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">7d</span><span className="tabular-nums">{r.change7d.toFixed(1)}%</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">30d</span><span className="tabular-nums">{r.change30d.toFixed(1)}%</span></p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <RarityBadge code={r.rarity} />
              </div>
            </div>
          ))}
        </div>

        <OverlayChart rows={rows} />
      </div>
    </div>
  );
}
